import React, { useState, useEffect } from 'react';
import { menuCategoriesAPI } from '../../services/api';

function MenuCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    display_order: 0
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await menuCategoriesAPI.getAll();
      setCategories(response.data.data);
    } catch (err) {
      setError('Failed to load categories');
      console.error('Categories error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingCategory) {
        await menuCategoriesAPI.update(editingCategory.id, formData);
      } else {
        await menuCategoriesAPI.create(formData);
      }
      
      await fetchCategories();
      handleCloseModal();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save category';
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      display_order: category.display_order
    });
    setShowModal(true);
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Are you sure you want to delete "${category.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await menuCategoriesAPI.delete(category.id);
      await fetchCategories();
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to delete category';
      alert(message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      display_order: 0
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'display_order' ? parseInt(value) || 0 : value
    }));
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Loading categories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
        <button className="btn btn-outline-danger ms-3" onClick={fetchCategories}>
          <i className="fas fa-redo me-2"></i>Retry
        </button>
      </div>
    );
  }

  return (
    <div className="menu-categories">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="page-title">Menu Categories</h2>
          <p className="page-subtitle">
            Organize your menu items into categories for better navigation
          </p>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => setShowModal(true)}
        >
          <i className="fas fa-plus me-2"></i>
          Add Category
        </button>
      </div>

      {/* Categories Grid */}
      {categories.length > 0 ? (
        <div className="row">
          {categories.map((category) => (
            <div key={category.id} className="col-md-6 col-lg-4 mb-4">
              <div className="category-card">
                <div className="category-header">
                  <h5 className="category-name">{category.name}</h5>
                  <div className="category-actions">
                    <button 
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => handleEdit(category)}
                      title="Edit Category"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-danger ms-2"
                      onClick={() => handleDelete(category)}
                      title="Delete Category"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
                
                <div className="category-body">
                  {category.description && (
                    <p className="category-description">{category.description}</p>
                  )}
                  
                  <div className="category-stats">
                    <div className="stat-item">
                      <i className="fas fa-utensils me-2 text-primary"></i>
                      <span>{category.item_count} items</span>
                    </div>
                    <div className="stat-item">
                      <i className="fas fa-sort-numeric-up me-2 text-secondary"></i>
                      <span>Order: {category.display_order}</span>
                    </div>
                  </div>
                </div>

                <div className="category-footer">
                  <small className="text-muted">
                    Slug: <code>{category.slug}</code>
                  </small>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state text-center py-5">
          <i className="fas fa-list fa-4x text-muted mb-3"></i>
          <h4 className="text-muted">No Categories Found</h4>
          <p className="text-muted mb-4">
            Start by creating your first menu category to organize your items
          </p>
          <button 
            className="btn btn-primary"
            onClick={() => setShowModal(true)}
          >
            <i className="fas fa-plus me-2"></i>
            Create First Category
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseModal}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Category Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., Breakfast, Main Course, Beverages"
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="3"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Brief description of this category"
                    ></textarea>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Display Order</label>
                    <input
                      type="number"
                      className="form-control"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="0"
                    />
                    <div className="form-text">
                      Lower numbers appear first in the menu
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        {editingCategory ? 'Update' : 'Create'} Category
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showModal && <div className="modal-backdrop fade show"></div>}

      <style jsx>{`
        .page-title {
          color: #2c3e50;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #6c757d;
          margin-bottom: 0;
        }

        .category-card {
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          overflow: hidden;
          height: 100%;
        }

        .category-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }

        .category-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .category-name {
          margin: 0;
          font-weight: 600;
        }

        .category-actions .btn {
          border-color: rgba(255, 255, 255, 0.3);
          color: white;
        }

        .category-actions .btn:hover {
          background-color: rgba(255, 255, 255, 0.2);
          border-color: white;
        }

        .category-body {
          padding: 1.5rem;
          flex-grow: 1;
        }

        .category-description {
          color: #6c757d;
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }

        .category-stats {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .stat-item {
          display: flex;
          align-items: center;
          font-size: 0.9rem;
          color: #495057;
        }

        .category-footer {
          padding: 1rem 1.5rem;
          background-color: #f8f9fa;
          border-top: 1px solid #e9ecef;
        }

        .empty-state {
          background: white;
          border-radius: 15px;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
          margin: 2rem 0;
        }

        .modal {
          z-index: 1050;
        }

        .modal-backdrop {
          z-index: 1040;
        }

        .modal-content {
          border: none;
          border-radius: 15px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .modal-header {
          background-color: #f8f9fa;
          border-bottom: 1px solid #e9ecef;
          border-radius: 15px 15px 0 0;
        }

        .modal-title {
          color: #2c3e50;
          font-weight: 600;
        }

        .form-control:focus {
          border-color: #c9302c;
          box-shadow: 0 0 0 0.2rem rgba(201, 48, 44, 0.25);
        }
      `}</style>
    </div>
  );
}

export default MenuCategories;